import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { intersection, reduce } from 'lodash';
import Fa from 'react-fontawesome';
import Joi from 'joi';
import { Field, reduxForm, SubmissionError, formValueSelector } from 'redux-form';
import { Modal, Button, FormGroup, FormControl, ControlLabel, HelpBlock } from 'react-bootstrap';
import SearchPath from '../../../../components/search-path';
import PathDisplay from '../../../../components/search-path/path';

import {
  fetchAllFeedbackLanguageSettings,
  orderFeedbackLanguageSettings,
  createFeedbackLanguageSettings,
  updateFeedbackLanguageSettings,
} from '../../SetupActions';

class AddLanguageForm extends Component {
  constructor(props) {
    super(props);
    this.state = {

    };
    this.closeModal = this.closeModal.bind(this);
    this.updateSearchPath = this.updateSearchPath.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.filterNodes = this.filterNodes.bind(this);
    this.edit = this.edit.bind(this);
    this.renderSearchPathField = this.renderSearchPathField.bind(this);
  }
  
  componentWillReceiveProps(nextProps) {
    if (nextProps.show && !this.props.show) {
      this.props.initialize({ ...this.props.settings });
    } else if (!nextProps.show && this.props.show) {
      this.props.initialize({});
    }
  }

  closeModal() {
    this.props.onHide();
    this.props.change('search_paths', []);
  }

  updateSearchPath(paths) {
    this.props.change('search_paths', paths);
  }

  handleSubmit(data) {
    const tasks = data._id ? updateFeedbackLanguageSettings(data._id, data) : createFeedbackLanguageSettings(data);
    return this.props.dispatch(tasks).then(
      (results) => {
        this.props.onSuccess(results);
        this.closeModal();
      },
      (errors) => {
        const e = errors.data || errors;
        if (e.message) {
          e._error = e.message;
        }
        throw new SubmissionError(e);
      },
    );
  }

  edit() {
  }


  filterNodes(node) {
    const { fields } = node;
    return reduce(fields, (items, field) => {
      const validReference = intersection(field.reference_to, ['account', 'Account', 'User', 'systemuser', 'contact', 'Contact']).length > 0;
      const validType = field.type === 'picklist';

      const isValid = validType || validReference;
      if (isValid) {
        items.push(field);
      }
      return items;
    }, []);
  }

  renderSearchPathField({
    label, hidden, disabled, meta: { touched, error },
  }) {
    const { submitting, selectedSearchPaths, selectedSource } = this.props;

    const validationState = touched && error ? 'error' : null;

    const isAccount = selectedSource === 'account';

    return (
      <FormGroup validationState={validationState}>
        {label && label !== '' && <ControlLabel>{label}</ControlLabel>}
        <SearchPath
          paths={selectedSearchPaths}
          disabled={submitting || disabled}
          onUpdate={this.updateSearchPath}
          selectedSearchPaths={selectedSearchPaths}
          filterNodes={this.filterNodes}
          startingNode={isAccount ? 'Account' : 'Opportunity'}
        />
        {hidden !== true && <FormControl.Feedback />}
        {touched && error && <HelpBlock>{error}</HelpBlock>}
      </FormGroup>
    );
  }

  render() {
    const { 
      show, selectedSource, handleSubmit, submitting, valid,
      selectedId,
    } = this.props;
    const disabled = false;
    const isEditing = selectedId !== null && selectedId !== undefined;
    return (
      <Modal show={show} onHide={this.closeModal} keyboard={!disabled}>
        <form onSubmit={handleSubmit(this.handleSubmit)}>
          <Modal.Header closeButton>
            <Modal.Title className="text-capitalize">Add new {selectedSource} based language mappings</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div>
              <Field
                name="search_paths"
                id="label-field"
                disabled={submitting}
                type="text"
                hidden
                component={this.renderSearchPathField}
                label="Target Field"
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <hr />
            <div className="clearfix">
              <Button onClick={this.closeModal} className="pull-left" bsStyle="default">Cancel</Button>
              <Button bsStyle="primary" disabled={submitting || !valid} type="submit">
                {isEditing &&
                  <span>
                    {!submitting && <span><Fa name="edit" /> Update</span>}
                    {submitting && <span><Fa name="spinner" spin /> Updating</span>}
                  </span>
                }
                {!isEditing &&
                  <span>
                    {!submitting && <span><Fa name="plus" /> Submit</span>}
                    {submitting && <span>Submiting</span>}
                  </span>
                }
              </Button>
              
            </div>
          </Modal.Footer>
        </form>
      </Modal>
    );
  }
}

/** Retrieve data from store as props */
const selector = formValueSelector('feedbackLanguageSettings');
function mapStateToProps(state) {
  return {
    selectedSearchPaths: selector(state, 'search_paths'),
    selectedId: selector(state, '_id'),
    selectedSource: selector(state, 'source'),
  };
}

AddLanguageForm.propTypes = {
  dispatch: PropTypes.func.isRequired,
  show: PropTypes.bool,
  onShow: PropTypes.func,
  onHide: PropTypes.func,
  submitting: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  valid: PropTypes.bool.isRequired,
  change: PropTypes.func.isRequired,
  selectedSearchPaths: PropTypes.arrayOf(PropTypes.string),
  selectedId: PropTypes.string,
  selectedSource: PropTypes.string.isRequired,
  settings: PropTypes.shape({
    _id: PropTypes.string,
    search_paths: PropTypes.arrayOf(PropTypes.string),
    source: PropTypes.string.isRequired,
  }).isRequired,
  onSuccess: PropTypes.func,
};
AddLanguageForm.defaultProps = {
  show: false,
  onShow: () => {},
  onHide: () => {},
  selectedSearchPaths: [],
  selectedId: null,
  onSuccess: () => {},
};
AddLanguageForm.contextTypes = {
};

const validate = (values) => {
  // Legacy constraints for reference:
  // const constraints = {
  //   search_paths: {
  //     presence: { allowEmpty: false },
  //   },
  //   source: {
  //     presence: { allowEmpty: false },
  //   },
  // };

  const schema = Joi.object({
    search_paths: Joi.array().min(1).required().messages({
      'array.base': 'Search paths must be an array',
      'array.min': 'At least one search path is required',
      'any.required': 'Search paths are required',
    }),
    source: Joi.string().required().messages({
      'string.base': 'Source must be a string',
      'string.empty': 'Source is required',
      'any.required': 'Source is required',
    }),
  }).unknown(true);

  const { error } = schema.validate(values, { abortEarly: false });
  if (!error) return {};
  
  return error.details.reduce((acc, curr) => {
    if (!acc[curr.path[0]]) acc[curr.path[0]] = [];
    acc[curr.path[0]].push(curr.message);
    return acc;
  }, {});
};

const Form = reduxForm({
  form: 'feedbackLanguageSettings',
  validate,
}, { touchOnChange: true })(AddLanguageForm);



export default connect(mapStateToProps)(Form);
